import React, { useCallback, useContext, useEffect } from 'react'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import type { PaginatorState, BundledOfferState } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { CALENDAR_GA_EVENT_ACTION } from 'constants/analytics'
import TextButton from 'LuxKit/Button/TextButton'
import Caption from 'LuxKit/Typography/Caption'
import FormatCurrency from 'components/Common/FormatCurrency'
import Heading from 'LuxKit/Typography/Heading'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import Group from 'components/utils/Group'
import { isBundleAndSaveV2Enabled } from 'lib/offer/offerUtils'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import DropdownSheetFooter from 'LuxKit/Dropdown/Sheet/DropdownSheetFooter'
import type { BundleSelectedDaysInfo } from 'selectors/offerPage/bundledOfferPageSelectors'
import { getBundleSelectedDays } from 'selectors/offerPage/bundledOfferPageSelectors'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { useIntl } from 'react-intl'

interface Props {
  checkIn?: string
  offer: App.Offer
  fetching?: boolean
  selectedDay?: App.CalendarDay
  relevantPackages: Array<App.OfferPackageOptionView>
  selectedPackageView: App.OfferPackageOptionView
  otherAvailability: { [checkIn: string]: boolean }
  paginator?: PaginatorState
  complementaryBundleState?: BundledOfferState
  isCalendarAlreadyOpen?: boolean
  bundleSelectedDays: BundleSelectedDaysInfo
}

function OfferBundleAndSaveCalendarDatePickerDropdownFooter(props: Props) {
  const {
    checkIn,
    offer,
    fetching,
    selectedDay,
    relevantPackages,
    selectedPackageView,
    otherAvailability,
    paginator,
    complementaryBundleState,
    isCalendarAlreadyOpen,
    bundleSelectedDays,
  } = props

  const intl = useIntl()
  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)
  const dispatch = useOfferPageDispatch()

  useEffect(() => {
    if (!fetching && checkIn && selectedDay && !selectedDay.hotelAvailable && !otherAvailability[selectedDay.checkIn]) {
      // clear check in if the date we have selected is no longer available
      dispatch({ type: OfferPageActions.setCheckInDate, checkIn: undefined })
    }
  }, [checkIn, selectedDay]) // eslint-disable-line react-hooks/exhaustive-deps

  const onApply = useCallback(() => {
    if (complementaryBundleState && !complementaryBundleState?.checkInDate && complementaryBundleState.offer) {
      if (!isCalendarAlreadyOpen) {
        dispatch({ type: OfferPageActions.toggleSubFilter, filter: 'duration' })
      }
      dispatch({ type: OfferPageActions.setPaginatorCurrentOfferId, currentOfferId: complementaryBundleState.offer.id })
      return
    }

    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.Apply)
    dispatch({ type: OfferPageActions.closeFilter })
  }, [dispatch, onUserAnalyticsAction, complementaryBundleState, isCalendarAlreadyOpen])

  const onResetDates = useCallback(() => {
    dispatch({ type: OfferPageActions.resetDates, offerId: paginator?.currentOfferId })
  }, [dispatch, paginator])

  const useDynamicCancellationPolicies = offer.property?.useDynamicCancellationPolicies ?? false

  const resetDatesLabel = intl.formatMessage({
    id: 'dnJ2xVfiy3',
    defaultMessage: 'Reset dates',
    description: 'Button to reset selected dates in the bundle calendar picker',
  })
  const continueLabel = intl.formatMessage({
    id: 'iMU2sen+bH',
    defaultMessage: 'Continue',
    description: 'Button to continue after selecting dates in the bundle calendar picker',
  })

  const nightsTwoHotelsFromLabel = intl.formatMessage({
    id: 'l4oM+ij/2N',
    defaultMessage: '{nights, plural, one {# night} other {# nights}}, 2 hotels from',
    description: 'Bundle calendar footer caption with nights, 2 hotels, and from',
  }, { nights: bundleSelectedDays.duration })
  const nightsTwoHotelsLabel = intl.formatMessage({
    id: 'Xo8vRbqjK9',
    defaultMessage: '{nights, plural, one {# night} other {# nights}}, 2 hotels',
    description: 'Bundle calendar footer caption with nights and 2 hotels',
  }, { nights: bundleSelectedDays.duration })

  return <DropdownSheetFooter
    startSlot={bundleSelectedDays.available && <Group direction="vertical">
      <Caption variant="large">
        {relevantPackages.length !== 1 ? nightsTwoHotelsFromLabel : nightsTwoHotelsLabel}
      </Caption>
      <div style={{ display: 'inline-flex', gap: '0.25em', alignItems: 'baseline' }}>
        <Heading as="span" variant="heading5">
          <FormatCurrency value={bundleSelectedDays.hotelTotal} />
        </Heading>
        <Caption as="span" variant="large">
          {intl.formatMessage({
            id: '9ewXxh1nkd',
            defaultMessage: 'total',
            description: 'Total price label in bundle calendar footer',
          })}
        </Caption>
      </div>

      {isBundleAndSaveV2Enabled() && <PriceRowTaxesAndFees />}
    </Group>}
    actionsSlot={<>
      {!!selectedDay && <TextButton kind="tertiary" size="large" onClick={onResetDates}>
        {resetDatesLabel}
      </TextButton>}
      {isBundleAndSaveV2Enabled() && !!selectedDay && (!selectedPackageView || useDynamicCancellationPolicies) && <TextButton
        data-testid="offer-calendar-apply"
        kind="primary"
        size="large"
        onClick={onApply}
      >
        {continueLabel}
      </TextButton>}
      {!isBundleAndSaveV2Enabled() && !!selectedDay && (!selectedPackageView || useDynamicCancellationPolicies) && (
        <TextButton kind="primary" size="large" data-testid="offer-calendar-apply" onClick={onApply}>
          {continueLabel}
        </TextButton>
      )}
    </>}
  />
}

export default withOfferPageAndReduxState((state, appState) => ({
  bundleSelectedDays: getBundleSelectedDays(appState, state),
}), OfferBundleAndSaveCalendarDatePickerDropdownFooter)
